/**
 * AssignmentRoutinePreview — 출제 전에 "학생이 실제로 뭘 풀게 되는지"
 * 루틴을 순서대로 보여주는 검토용 패널. (ECHO 톤)
 *
 * 마지막 단계(학생 선택)에서 출제 버튼 위에 표시됩니다.
 * worksheet.type + selectedTypes + 복습 설정을 읽어 자동 구성.
 */

// analysis 유형의 학생 화면 설명
const ANALYSIS_ROUTINE = {
  keypoints: {
    name: "핵심정리",
    detail: "지문의 핵심 문장 빈칸을 채우며 내용을 정리합니다.",
  },
  reading: {
    name: "지문읽기",
    detail: "지문을 문장 단위로 읽습니다.",
    extra: (t) => (t.hide_translation ? "해석 숨김" : "해석 표시"),
  },
  words: {
    name: "단어학습",
    detail: "지문 속 단어의 뜻과 철자를 학습합니다.",
  },
  syntax_analysis: {
    name: "구문분석",
    detail: "문장 성분(주어·동사·목적어·보어)을 직접 표시합니다.",
    extra: (t) => (t.level?.value ? `난이도 ${t.level.value}` : null),
  },
  flow: {
    name: "흐름정리",
    detail: "문장 순서를 배열하며 글의 흐름을 파악합니다.",
    extra: (t) =>
      [t.kor && "한글", t.eng && "영어"].filter(Boolean).join(" · ") || null,
  },
  sentence_test: {
    name: "문장학습",
    detail: "문장을 유형별로 반복 연습합니다.",
    extra: (t) => {
      const opts = (t.options || []).map((o) => o.value).join(" · ");
      const parts = [opts];
      if (t.examIndex) parts.push(`${t.examIndex}번 세트`);
      if (t.is_dictation) parts.push("Dictation");
      return parts.filter(Boolean).join(" / ") || null;
    },
  },
};

// sentence(문장테스트) 유형 세트 이름
const SENTENCE_SET_NAMES = {
  blank: "빈칸 채우기 (순서 고정)",
  scramble: "스크램블 (순서 고정)",
  composition: "영작 (순서 고정)",
  translation: "해석 (순서 고정)",
  dictation: "받아쓰기 (순서 고정)",
  comprehensive: "종합 (순서 랜덤)",
};

const AssignmentRoutinePreview = ({
  worksheet,
  selectedTypes,
  selectedParagraphs,
  isRetryEnabled,
  isAutoRetryEnabled,
  maxRetries,
}) => {
  const steps = buildRoutine(worksheet, selectedTypes, selectedParagraphs);
  const retry = buildRetryInfo(
    worksheet,
    selectedTypes,
    isRetryEnabled,
    isAutoRetryEnabled,
    maxRetries
  );

  if (steps.length === 0 && !retry) return null;

  return (
    <div
      style={{
        border: "1px solid #E6EDEC",
        borderRadius: "14px",
        overflow: "hidden",
        backgroundColor: "#FFF",
      }}
    >
      <div
        style={{
          padding: "11px 16px",
          backgroundColor: "#F4F8F7",
          fontSize: "0.8rem",
          fontWeight: 800,
          letterSpacing: "0.04em",
          color: "#00A396",
        }}
      >
        학생에게 이렇게 출제됩니다
      </div>

      <div style={{ padding: "6px 16px 12px" }}>
        {steps.map((step, i) => (
          <div
            key={i}
            style={{
              display: "flex",
              gap: "12px",
              padding: "9px 0",
              borderBottom:
                i < steps.length - 1 ? "1px solid #F0F4F4" : "none",
              alignItems: "flex-start",
            }}
          >
            {/* 순번 */}
            <span
              style={{
                width: "22px",
                height: "22px",
                borderRadius: "999px",
                backgroundColor: "#0E141B",
                color: "#FFF",
                fontSize: "0.7rem",
                fontWeight: 800,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flexShrink: 0,
                marginTop: "1px",
                fontVariantNumeric: "tabular-nums",
              }}
            >
              {i + 1}
            </span>
            <div style={{ minWidth: 0 }}>
              <div
                style={{
                  fontSize: "0.88rem",
                  fontWeight: 700,
                  color: "#0E141B",
                  display: "flex",
                  alignItems: "center",
                  gap: "8px",
                  flexWrap: "wrap",
                }}
              >
                {step.name}
                {step.extra && (
                  <span
                    style={{
                      fontSize: "0.7rem",
                      fontWeight: 700,
                      color: "#00A396",
                      backgroundColor: "#E5FBF8",
                      borderRadius: "999px",
                      padding: "2px 9px",
                    }}
                  >
                    {step.extra}
                  </span>
                )}
              </div>
              <div
                style={{
                  fontSize: "0.78rem",
                  color: "#7A8490",
                  marginTop: "2px",
                  lineHeight: 1.5,
                }}
              >
                {step.detail}
              </div>
            </div>
          </div>
        ))}

        {/* 복습 안내 */}
        {retry && (
          <div
            style={{
              marginTop: "10px",
              padding: "10px 13px",
              backgroundColor: "#F4F8F7",
              borderRadius: "10px",
              fontSize: "0.78rem",
              color: "#3A434E",
              lineHeight: 1.55,
            }}
          >
            <span style={{ fontWeight: 800, color: "#00A396" }}>복습 </span>
            {retry}
          </div>
        )}
      </div>
    </div>
  );
};

/* ── 루틴 구성 ── */
function buildRoutine(worksheet, selectedTypes, selectedParagraphs) {
  const type = worksheet?.type;
  const paragraphCount = selectedParagraphs?.length || 0;

  if (type === "analysis") {
    // constants.jsx 순서 유지: keypoints → reading → words → syntax → flow → sentence_test
    const order = [
      "keypoints",
      "reading",
      "words",
      "syntax_analysis",
      "flow",
      "sentence_test",
    ];
    return order
      .filter((key) => selectedTypes.some((t) => t.key === key))
      .map((key) => {
        const sel = selectedTypes.find((t) => t.key === key);
        const def = ANALYSIS_ROUTINE[key];
        return {
          name: def.name,
          detail:
            paragraphCount > 1
              ? `선택한 지문 ${paragraphCount}개 각각에 대해 — ${def.detail}`
              : def.detail,
          extra: def.extra ? def.extra(sel) : null,
        };
      });
  }

  if (type === "vocabulary") {
    const exam = selectedTypes.find((t) => t.key === "exam");
    return [
      {
        name: "단어 테스트",
        detail: `주관식·객관식 단어 문제를 세트 순서대로 풉니다.`,
        extra: exam?.count ? `${exam.count}개 세트` : null,
      },
    ];
  }

  if (type === "sentence") {
    return selectedTypes
      .filter((t) => t.count > 0)
      .map((t) => ({
        name: SENTENCE_SET_NAMES[t.key] || t.key,
        detail: "문장을 듣고/보고 순서대로 풉니다.",
        extra: `${t.count}세트`,
      }));
  }

  if (type === "comprehensive") {
    const comp = selectedTypes.find((t) => t.key === "comprehensive");
    return [
      {
        name: "내용이해 문제풀이",
        detail: `선택한 지문 ${paragraphCount}개의 내용이해 문제를 풉니다.`,
        extra: comp?.index ? `${comp.index}번 세트` : null,
      },
    ];
  }

  if (type === "grammar") {
    return [
      {
        name: "어법 테스트",
        detail: "어법 문제 전체를 순서대로 풉니다.",
      },
    ];
  }

  if (type === "paragraph") {
    return [
      {
        name: "모의고사",
        detail: `선택한 지문 ${paragraphCount}개의 모의고사 문제를 풉니다.`,
      },
    ];
  }

  return [];
}

/* ── 복습 설명 문구 ── */
function buildRetryInfo(
  worksheet,
  selectedTypes,
  isRetryEnabled,
  isAutoRetryEnabled,
  maxRetries
) {
  const type = worksheet?.type;

  let enabled, auto, max;
  if (type === "vocabulary") {
    const exam = selectedTypes.find((t) => t.key === "exam");
    if (!exam) return null;
    enabled = exam.isRetryEnabled !== false;
    auto = exam.isAutoRetryEnabled === true;
    max = exam.maxRetries;
  } else if (type === "paragraph") {
    enabled = isRetryEnabled;
    auto = isAutoRetryEnabled;
    max = maxRetries;
  } else {
    return null; // 복습 설정이 없는 유형
  }

  if (!enabled) return "복습 없이 1회만 풀고 종료됩니다.";

  const countText =
    max === -1 || max === undefined
      ? "모든 문제를 맞힐 때까지 반복"
      : `최대 ${max}회까지 반복`;
  const autoText = auto
    ? "제출 즉시 틀린 문제가 자동으로 복습 과제로 등록되고,"
    : "선생님이 복습 문제지 목록에서 직접 출제하면,";

  return `${autoText} 틀린 문제만 다시 풉니다. ${countText}됩니다.`;
}

export default AssignmentRoutinePreview;
